import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { CreditCard, CheckCircle, Plus, Pencil, Trash, Lock, ShoppingCart, Hourglass, Sparkle, Coins, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import BackButton from "@/components/app/BackButton";
import GrantPackageDialog from "@/components/app/GrantPackageDialog";

const fmtPrice = (cents, currency = "usd") => {
  if (cents == null) return "—";
  const dollars = cents / 100;
  return dollars.toLocaleString(undefined, { style: "currency", currency: currency.toUpperCase() });
};

export default function Billing() {
  const { user } = useAuth();
  const isCoach = user?.role === "admin" || user?.role === "coach";
  const [pkgTypes, setPkgTypes] = useState([]);
  const [myPackages, setMyPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // package_type_id currently buying
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: types }, mine] = await Promise.all([
        api.get("/package-types", { params: { active_only: !isCoach } }),
        user?.role === "athlete"
          ? api.get(`/athletes/${user.id}/packages`)
          : Promise.resolve({ data: [] }),
      ]);
      setPkgTypes(types);
      setMyPackages(mine.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [user?.id]);

  const buy = async (pkg) => {
    setBusy(pkg.id);
    try {
      const { data } = await api.post("/checkout/session", {
        package_type_id: pkg.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(null);
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-5xl mx-auto space-y-8">
      <BackButton />
      <div>
        <div className="hs-label">Class packs</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 flex items-center gap-3">
          <CreditCard size={28} className="text-moss" weight="fill" />
          {isCoach ? "Class packs" : "Your class packs"}
        </h1>
        <p className="text-ink-2 mt-1 text-sm">
          {isCoach
            ? "Athletes purchase from these packs through Stripe. Manage available packs in the Admin → Packages page."
            : "Each session your coach checks you in to deducts one credit from an active pack."}
        </p>
      </div>

      {user?.role === "athlete" && (
        <section className="space-y-3">
          <div className="hs-label text-[11px]">Your active packs</div>
          {myPackages.length === 0 ? (
            <div className="hs-card p-6 text-center text-ink-3" data-testid="no-packages">
              No active packs right now. Reach out to your coach to get set up with one.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {myPackages.map((p) => (
                <ActivePackageCard key={p.id} pkg={p} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Pricing tiles are coach/admin only — athletes never see prices or buy buttons. */}
      {isCoach && (
        <section className="space-y-3">
          <div className="hs-label text-[11px]">Available packs</div>
          {loading ? (
            <div className="hs-card p-6 text-ink-3 text-center">Loading…</div>
          ) : pkgTypes.length === 0 ? (
            <div className="hs-card p-6 text-center text-ink-3">
              No packs configured yet — create one in Admin → Packages.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="package-list">
              {pkgTypes.map((p) => (
                <PackageCard
                  key={p.id}
                  pkg={p}
                  canBuy={false}
                  buying={false}
                  onBuy={() => {}}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function ActivePackageCard({ pkg, coachView, onChanged }) {
  const navigate = useNavigate();
  const expired = pkg.expires_at && new Date(pkg.expires_at) < new Date();
  const empty = pkg.sessions_remaining <= 0;
  const isCredit = pkg.payment_status === "credit";
  const accent = empty || expired ? "text-ink-3" : (isCredit ? "text-warning" : "text-moss");

  const markPaid = async () => {
    if (!coachView) return;
    const method = window.prompt("Payment method? (cash / check / yappy / ach / zelle / bank / credit / other)", "cash");
    if (!method) return;
    const reference = window.prompt("Reference? (e.g., check #, txn id) — optional, press OK to skip", "") || "";
    try {
      await api.patch(`/athletes/${pkg.athlete_id}/packages/${pkg.id}/payment`, {
        payment_status: "paid",
        payment_method: method.trim().toLowerCase(),
        payment_reference: reference.trim() || null,
      });
      toast.success("Marked as paid");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className={`hs-card p-4 ${isCredit ? "border-warning/40 bg-warning/5" : ""}`} data-testid={`active-pkg-${pkg.id}`}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-heading text-base font-semibold">{pkg.package_name || "Class pack"}</h3>
        <span className={`font-data text-xs ${expired ? "text-error font-bold" : "text-ink-3"}`}>
          {expired ? "Expired" : `Expires ${formatDate(pkg.expires_at)}`}
        </span>
      </div>
      <div className={`mt-3 font-data text-3xl font-semibold ${accent}`}>
        {pkg.sessions_remaining} <span className="text-base text-ink-3 font-normal">/ {pkg.sessions_total}</span>
      </div>
      <div className="mt-1 text-xs text-ink-3">credits remaining</div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {isCredit ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-warning/15 text-warning text-[10px] font-bold uppercase tracking-[0.15em]">
            <Coins size={10} weight="fill" /> On credit
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sage text-moss text-[10px] font-bold uppercase tracking-[0.15em]">
            <Receipt size={10} weight="fill" /> Paid {pkg.payment_method ? `· ${pkg.payment_method}` : ""}
          </span>
        )}
        {pkg.source === "manual" && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted2 text-ink-2 text-[10px] font-bold uppercase tracking-[0.15em]">
            <Sparkle size={10} weight="fill" /> Coach grant
          </span>
        )}
        {pkg.source === "stripe" && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted2 text-ink-2 text-[10px] font-bold uppercase tracking-[0.15em]">
            Stripe
          </span>
        )}
        {pkg.payment_reference && (
          <span className="text-[10px] text-ink-3 font-data">ref: {pkg.payment_reference}</span>
        )}
      </div>
      {coachView && isCredit && (
        <Button
          onClick={markPaid}
          size="sm"
          className="mt-3 rounded-xl h-9 bg-moss hover:bg-moss-hover text-white hover:text-white"
          data-testid={`mark-paid-${pkg.id}`}
        >
          <Receipt size={14} className="mr-1.5" weight="fill" /> Mark paid
        </Button>
      )}
      {coachView && (
        <Button
          onClick={() => navigate(`/athletes/${pkg.athlete_id}/packs/${pkg.id}`)}
          size="sm"
          variant="outline"
          className={`rounded-xl h-9 ${isCredit ? "mt-2 ml-2" : "mt-3"}`}
          data-testid={`manage-pkg-${pkg.id}`}
        >
          <Pencil size={14} className="mr-1.5" /> Manage
        </Button>
      )}
    </div>
  );
}

function PackageCard({ pkg, canBuy, buying, onBuy }) {
  return (
    <div className={`hs-card p-5 flex flex-col ${pkg.active ? "" : "opacity-60"}`} data-testid={`pkg-card-${pkg.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-heading text-lg font-semibold leading-tight">{pkg.name}</h3>
          {pkg.description && <p className="mt-1 text-sm text-ink-2">{pkg.description}</p>}
        </div>
        {!pkg.active && (
          <span className="px-2 py-0.5 rounded-md bg-muted2 text-ink-3 text-[10px] font-bold uppercase">Inactive</span>
        )}
      </div>
      <div className="mt-4 font-data">
        <div className="text-3xl font-semibold text-ink">{fmtPrice(pkg.price_cents, pkg.currency)}</div>
        <div className="mt-1 text-sm text-ink-2">
          {pkg.sessions} sessions · valid {pkg.expires_in_months} {pkg.expires_in_months === 1 ? "month" : "months"}
        </div>
      </div>
      <div className="flex-1" />
      {canBuy ? (
        <Button
          onClick={onBuy}
          disabled={buying}
          className="mt-5 w-full rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white h-11"
          data-testid={`buy-pkg-${pkg.id}`}
        >
          {buying ? (<><Hourglass size={14} className="mr-2" /> Redirecting…</>) :
                    (<><ShoppingCart size={14} className="mr-2" weight="fill" /> Buy now</>)}
        </Button>
      ) : (
        <div className="mt-5 text-xs text-ink-3 text-center font-data">
          {pkg.active ? "Athlete purchase only" : "Disabled by coach"}
        </div>
      )}
    </div>
  );
}

// ─── Stripe success page ─────────────────────────────────────────────────────

export function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | pending | failed
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        if (attempts >= 8) {
          setStatus("pending");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="p-6 sm:p-10 max-w-xl mx-auto" data-testid="billing-success-page">
      {status === "checking" && (
        <div className="hs-card p-10 text-center">
          <Hourglass size={32} className="mx-auto text-moss animate-pulse" />
          <h2 className="mt-3 font-heading text-2xl font-semibold">Confirming your payment…</h2>
          <p className="mt-2 text-ink-2 text-sm">Hang tight, this can take a few seconds.</p>
        </div>
      )}
      {status === "paid" && (
        <div className="hs-card p-10 text-center" data-testid="billing-success">
          <div className="h-16 w-16 rounded-full bg-sage grid place-items-center mx-auto">
            <CheckCircle size={32} className="text-moss" weight="fill" />
          </div>
          <h2 className="mt-4 font-heading text-2xl font-semibold">You're in!</h2>
          <p className="mt-2 text-ink-2">
            {info?.sessions
              ? `${info.sessions} sessions added to your account${info.package_name ? ` (${info.package_name})` : ""}.`
              : "Your purchase was successful."}
          </p>
          <Button
            onClick={() => navigate("/billing")}
            className="mt-6 rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white"
            data-testid="back-to-billing"
          >
            View my packs
          </Button>
        </div>
      )}
      {status === "pending" && (
        <div className="hs-card p-10 text-center">
          <Hourglass size={32} className="mx-auto text-warning" />
          <h2 className="mt-3 font-heading text-2xl font-semibold">Payment is processing…</h2>
          <p className="mt-2 text-ink-2 text-sm">Stripe will confirm shortly. Refresh in a minute, or check your packs page.</p>
          <Button onClick={() => navigate("/billing")} className="mt-5 rounded-xl">Back to packs</Button>
        </div>
      )}
      {status === "failed" && (
        <div className="hs-card p-10 text-center">
          <div className="h-16 w-16 rounded-full bg-red-100 grid place-items-center mx-auto">
            <Lock size={28} className="text-error" />
          </div>
          <h2 className="mt-4 font-heading text-2xl font-semibold">Payment didn't complete.</h2>
          <p className="mt-2 text-ink-2">No charge was made. Try again from the packs page.</p>
          <Button onClick={() => navigate("/billing")} className="mt-5 rounded-xl">Back to packs</Button>
        </div>
      )}
    </div>
  );
}

// ─── Coach view: per-athlete billing (packs + history) ───────────────────────

export function AthleteBilling() {
  const params = useParams();
  const athleteId = params.athleteId;
  const [athlete, setAthlete] = useState(null);
  const [packs, setPacks] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, p, att] = await Promise.all([
        api.get(`/users/${athleteId}`),
        api.get(`/athletes/${athleteId}/packages`),
        api.get("/attendance", { params: { athlete_id: athleteId } }),
      ]);
      setAthlete(a.data);
      setPacks(p.data);
      setAttendance(att.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [athleteId]);

  const totalCreditOwed = packs
    .filter((p) => p.payment_status === "credit")
    .reduce((sum, p) => sum + (p.amount_paid_cents || 0), 0);

  return (
    <div className="p-6 sm:p-10 max-w-5xl mx-auto space-y-8">
      <BackButton />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="hs-label">Billing</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2">
            {athlete ? athlete.name : "Athlete"}
          </h1>
          <p className="text-ink-2 text-sm mt-1">Manage class packs, payments, and attendance.</p>
        </div>
        {athlete && (
          <GrantPackageDialog
            athleteId={athleteId}
            athleteName={athlete.name}
            onGranted={load}
            trigger={
              <Button className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white" data-testid="grant-pack-button">
                <Sparkle size={14} className="mr-1.5" weight="fill" /> Grant pack
              </Button>
            }
          />
        )}
      </div>

      {totalCreditOwed > 0 && (
        <div className="hs-card p-4 border-warning/40 bg-warning/5 flex items-center gap-3" data-testid="credit-owed-banner">
          <Coins size={22} weight="fill" className="text-warning shrink-0" />
          <div className="flex-1">
            <div className="font-heading text-base font-semibold">Outstanding balance</div>
            <p className="text-sm text-ink-2">
              {fmtPrice(totalCreditOwed)} owed across {packs.filter((p) => p.payment_status === "credit").length} pack(s) on credit.
            </p>
          </div>
        </div>
      )}

      <section>
        <div className="hs-label text-[11px] mb-3">All packs ({packs.length})</div>
        {loading ? (
          <div className="hs-card p-6 text-ink-3 text-center">Loading…</div>
        ) : packs.length === 0 ? (
          <div className="hs-card p-10 text-center">
            <CreditCard size={28} className="mx-auto text-ink-3" />
            <p className="mt-3 text-ink-2">No packs yet. Grant the first one above.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {packs.map((p) => (
              <ActivePackageCard key={p.id} pkg={p} coachView onChanged={load} />
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="hs-label text-[11px] mb-3">Attendance ({attendance.length} sessions)</div>
        {attendance.length === 0 ? (
          <div className="hs-card p-6 text-center text-ink-3">No check-ins yet.</div>
        ) : (
          <div className="hs-card overflow-hidden">
            <table className="w-full font-data text-sm">
              <thead className="bg-muted2 text-ink-2">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Date</th>
                  <th className="text-left px-5 py-3 font-medium">Status</th>
                  <th className="text-left px-5 py-3 font-medium">Credit</th>
                </tr>
              </thead>
              <tbody>
                {attendance.slice(0, 50).map((att) => {
                  const pack = att.athlete_package_id
                    ? packs.find((p) => p.id === att.athlete_package_id)
                    : null;
                  const owed = pack && pack.payment_status === "credit";
                  return (
                    <tr key={att.id} className="border-t border-line">
                      <td className="px-5 py-3 font-semibold">{formatDate(att.date)}</td>
                      <td className="px-5 py-3">
                        {att.restored_at ? (
                          <span className="text-ink-3">Undone {formatDate(att.restored_at)}</span>
                        ) : (
                          <span className="text-moss">Checked in</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-ink-2">
                        {att.credit_consumed ? (
                          owed ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.14em] bg-warning/15 text-warning"
                              data-testid={`attendance-to-be-paid-${att.id}`}
                              title={`This session is credited to '${pack.package_name}', which is unpaid.`}
                            >
                              To Be Paid
                            </span>
                          ) : (
                            "−1 credit"
                          )
                        ) : (
                          "Free / no pack"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Coach view: per-athlete billing (packs + history) ─── END

// ─── Admin: package types CRUD ───────────────────────────────────────────────

export function AdminPackages() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/package-types");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSave = async (form) => {
    try {
      if (editing) {
        await api.patch(`/package-types/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/package-types", form);
        toast.success("Created");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this package type? Existing athlete packs are NOT deleted.")) return;
    try {
      await api.delete(`/package-types/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-5xl mx-auto space-y-6">
      <BackButton />
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="hs-label">Admin</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2">Class packs</h1>
          <p className="text-ink-2 text-sm mt-1">Define what athletes can purchase. Prices charged through Stripe Checkout.</p>
        </div>
        <Button
          onClick={() => { setEditing(null); setOpen(true); }}
          className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white"
          data-testid="new-package-type"
        >
          <Plus size={14} className="mr-2" weight="bold" /> New pack
        </Button>
      </div>

      {loading ? (
        <div className="hs-card p-6 text-ink-3 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="hs-card p-10 text-center">
          <CreditCard size={28} className="mx-auto text-ink-3" />
          <p className="mt-3 text-ink-2">No packs yet.</p>
        </div>
      ) : (
        <div className="hs-card overflow-hidden">
          <table className="w-full font-data text-sm">
            <thead className="bg-muted2 text-ink-2">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Name</th>
                <th className="text-left px-5 py-3 font-medium">Sessions</th>
                <th className="text-left px-5 py-3 font-medium">Price</th>
                <th className="text-left px-5 py-3 font-medium">Validity</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-right px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-line" data-testid={`pkg-row-${p.id}`}>
                  <td className="px-5 py-3 font-semibold text-ink">
                    {p.name}
                    {p.pay_per_session && (
                      <span
                        className="ml-2 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.14em] bg-warning/15 text-warning align-middle"
                        data-testid={`pkg-pay-per-session-badge-${p.id}`}
                      >
                        Pay per session
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-ink-2">{p.sessions}</td>
                  <td className="px-5 py-3 text-ink-2">{fmtPrice(p.price_cents, p.currency)}</td>
                  <td className="px-5 py-3 text-ink-2">{p.expires_in_months} mo</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] ${p.active ? "bg-sage text-moss" : "bg-muted2 text-ink-3"}`}>
                      {p.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right space-x-1">
                    <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-pkg-${p.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-lg text-error hover:bg-red-50 hover:text-error" onClick={() => del(p.id)} data-testid={`delete-pkg-${p.id}`}>
                      <Trash size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit pack" : "New pack"}</DialogTitle>
            <DialogDescription>Pricing is charged in USD by default. Athletes will see this exactly.</DialogDescription>
          </DialogHeader>
          <PackageTypeForm initial={editing} onSave={onSave} onCancel={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PackageTypeForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(() => initial ? {
    name: initial.name,
    description: initial.description || "",
    sessions: initial.sessions,
    price_dollars: (initial.price_cents / 100).toFixed(2),
    currency: initial.currency || "usd",
    expires_in_months: initial.expires_in_months,
    active: initial.active,
    pay_per_session: !!initial.pay_per_session,
  } : {
    name: "", description: "", sessions: 10, price_dollars: "200.00",
    currency: "usd", expires_in_months: 3, active: true, pay_per_session: false,
  });
  const update = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          name: f.name.trim(),
          description: f.description?.trim() || null,
          sessions: Number(f.sessions),
          price_cents: Math.round(Number(f.price_dollars) * 100),
          currency: (f.currency || "usd").toLowerCase(),
          expires_in_months: Number(f.expires_in_months),
          active: !!f.active,
          pay_per_session: !!f.pay_per_session,
        });
      }}
      className="space-y-4"
      data-testid="pkg-form"
    >
      <div>
        <Label className="hs-label">Name</Label>
        <Input required value={f.name} onChange={(e) => update("name", e.target.value)} className="mt-2 h-11 rounded-xl" placeholder="10-pack" data-testid="pkg-name" />
      </div>
      <div>
        <Label className="hs-label">Description (optional)</Label>
        <Textarea rows={2} value={f.description} onChange={(e) => update("description", e.target.value)} className="mt-2 rounded-xl" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="hs-label">Sessions</Label>
          <Input required type="number" min="1" value={f.sessions} onChange={(e) => update("sessions", e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="pkg-sessions" />
        </div>
        <div>
          <Label className="hs-label">Price (USD)</Label>
          <Input required type="number" min="1" step="0.01" value={f.price_dollars} onChange={(e) => update("price_dollars", e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="pkg-price" />
        </div>
        <div>
          <Label className="hs-label">Validity (months)</Label>
          <Input required type="number" min="1" value={f.expires_in_months} onChange={(e) => update("expires_in_months", e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="pkg-validity" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input id="pkg-active" type="checkbox" checked={!!f.active} onChange={(e) => update("active", e.target.checked)} className="h-4 w-4" data-testid="pkg-active" />
        <Label htmlFor="pkg-active" className="text-sm">Available for purchase by athletes</Label>
      </div>
      <div className="flex items-start gap-2 rounded-xl bg-muted2/60 p-3">
        <input id="pkg-pps" type="checkbox" checked={!!f.pay_per_session} onChange={(e) => update("pay_per_session", e.target.checked)} className="h-4 w-4 mt-0.5" data-testid="pkg-pay-per-session" />
        <div>
          <Label htmlFor="pkg-pps" className="text-sm font-semibold">Pay-per-session</Label>
          <p className="text-xs text-ink-3 mt-0.5">
            Grants default to <b>Credit · owed</b>. Every check-in is tagged <b>“To Be Paid”</b> until you mark the pack collected.
          </p>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel} className="rounded-xl">Cancel</Button>
        <Button type="submit" className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white" data-testid="save-pkg">Save</Button>
      </DialogFooter>
    </form>
  );
}
